/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { init, addBreadcrumb } from '@sentry/react';
import type {
  ErrorEvent,
  EventHint,
  Breadcrumb,
  BreadcrumbHint,
} from '@sentry/core';
import type { Store } from 'easy-peasy';

import config from '../config';
import type { State } from '../reducers';

// Marks an error as an expected/benign outcome (e.g. a 404 that just means
// "not provisioned yet") so beforeSend can silence it without matching on
// message text, which drifts silently if the message ever changes at the
// throw site. Mutates and returns the same error so the marker survives
// being rethrown up through intermediate catch blocks.
const EXPECTED_SENTRY_SILENCE = Symbol('expectedSentrySilence');

export function markExpectedError<E extends Error>(err: E): E {
  (err as Error & { [EXPECTED_SENTRY_SILENCE]?: true })[
    EXPECTED_SENTRY_SILENCE
  ] = true;
  return err;
}

function isExpectedError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as Error & { [EXPECTED_SENTRY_SILENCE]?: true })[
      EXPECTED_SENTRY_SILENCE
    ] === true
  );
}

// Token-bearing keys that must never reach Sentry, wherever they appear.
const SENSITIVE_KEYS = new Set([
  'accessToken',
  'refreshToken',
  'token',
  'idToken',
]);

// Sentry rejects an oversized envelope with a 413 and drops the whole event -
// stack trace included - so an unbounded state dump loses exactly the reports
// worth having: the ones from recordings whose metadata is big or malformed
// enough to break the viewer. These caps keep the dump useful but bounded.
const MAX_ARRAY_ENTRIES = 50;
const MAX_STRING_CHARS = 2_048;
const MAX_DEPTH = 8;
// Well under Sentry's ~1MB envelope limit, leaving headroom for the stack
// trace, breadcrumbs and request context, all of which beat the state dump.
const MAX_STATE_DUMP_CHARS = 128 * 1_024;

// SA3-AUTH-17: recursively redact token fields anywhere in the redux state. The
// previous shallow filter only stripped top-level keys of the `user` slice, but
// the live JWT lives at state.user.user.accessToken, so it was a no-op.
function deepRedact(
  value: unknown,
  seen = new WeakSet<object>(),
  depth = 0
): unknown {
  if (depth >= MAX_DEPTH) {
    return '[Truncated: max depth]';
  }
  if (typeof value === 'string' && value.length > MAX_STRING_CHARS) {
    return `${value.slice(0, MAX_STRING_CHARS)}[+${
      value.length - MAX_STRING_CHARS
    } chars]`;
  }
  // Typed arrays (LUTs, frame buffers) are objects but not Array.isArray, so
  // Object.entries would enumerate every index and serialize megabytes of
  // numbers. Summarize them instead - the values are never diagnostic.
  if (ArrayBuffer.isView(value)) {
    const { length } = value as unknown as { length: number };
    return `[${value.constructor.name} length=${length}]`;
  }
  if (Array.isArray(value)) {
    const kept = value
      .slice(0, MAX_ARRAY_ENTRIES)
      .map((item) => deepRedact(item, seen, depth + 1));
    return value.length > MAX_ARRAY_ENTRIES
      ? [...kept, `[+${value.length - MAX_ARRAY_ENTRIES} of ${value.length}]`]
      : kept;
  }
  if (value && typeof value === 'object') {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) =>
        SENSITIVE_KEYS.has(key)
          ? [key, '[REDACTED]']
          : [key, deepRedact(val, seen, depth + 1)]
      )
    );
  }
  return value;
}

// Query params that carry credentials (e.g. the desktop-handoff refresh token).
const SENSITIVE_URL_PARAMS = [
  'code',
  'authCode',
  'token',
  'access_token',
  'refresh_token',
];

// SA3-AUTH-16: redact credential query params from a breadcrumb URL before it is
// shipped. Sentry's fetch integration records data.url verbatim, which leaked
// the refresh token placed in the loopback GET auth-handoff URL.
function redactUrlSecrets(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    let changed = false;
    for (const param of SENSITIVE_URL_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[REDACTED]');
        changed = true;
      }
    }
    return changed ? parsed.toString() : rawUrl;
  } catch {
    return rawUrl;
  }
}

let store: Store<State> | undefined; // Updated on registering store.
const release = process.env.REACT_APP_RELEASE_ID || '11';
const isProd = process.env.NODE_ENV === 'production';

// This is the SOLE Sentry.init() call for the app. Do not add another
// init() elsewhere (e.g. index.tsx) - each call replaces the previous
// client wholesale rather than merging config, which would silently
// drop the beforeSend/beforeBreadcrumb hooks below.
if (isProd) {
  const sentryEnv = window.location.hostname.includes('studio.staging')
    ? 'staging'
    : 'production';

  init({
    dsn: config.sentry.dsn,
    release,
    environment: sentryEnv,
    sendDefaultPii: true,
    integrations: [],
    initialScope: {
      tags: { app: 'osw' },
    },
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    beforeBreadcrumb: (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => {
      const { category, data } = breadcrumb;
      // Ignore very noisy successful fetches of OSF frames / poses
      if (category === 'fetch' || category === 'xhr') {
        if (
          data &&
          data.status_code === 200 &&
          data.url &&
          data.url.indexOf('drive-osf') > -1
        ) {
          return null;
        }
      }

      // SA3-AUTH-16: strip credential query params before the breadcrumb ships.
      if (data && typeof data.url === 'string') {
        data.url = redactUrlSecrets(data.url);
      }

      return breadcrumb;
    },
    beforeSend: (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
      // Falls back to the already-built event's own exception value when the
      // original exception isn't an Error instance (e.g. a raw ErrorEvent
      // thrown by a vendored dependency's worker.onerror handler) - relying
      // solely on `instanceof Error` misses those entirely.
      const errorMessage =
        hint && hint.originalException instanceof Error
          ? hint.originalException.message
          : event.exception?.values?.[0]?.value || event.message || '';

      // Silence generic browser-native fetch failures (page navigated away
      // or reloaded mid-request, connectivity drop, content blocker, etc).
      // Each engine words its own "the network request never completed"
      // TypeError differently, but none carry an HTTP status to act on.
      const isGenericNetworkFailure =
        errorMessage === 'Failed to fetch' ||
        errorMessage === 'Load failed' ||
        errorMessage === 'NetworkError when attempting to fetch resource.';
      if (isGenericNetworkFailure) {
        return null;
      }

      // Silence keycloak-js's PKCE/UUID checks failing on browsers that
      // don't expose the Web Crypto API (old browsers, locked-down corporate
      // proxies, embedded webviews, bots hitting /login) - environmental,
      // not something this app can fix.
      if (errorMessage === 'Web Crypto API is not available.') {
        return null;
      }

      // Silence a vendored WASM SDK bug: its Emscripten-generated pthread
      // runtime assigns worker.onerror (not addEventListener) and rethrows
      // the raw ErrorEvent, which escapes Sentry's error-wrapping and gets
      // stringified into this useless message. Not fixable from app code -
      // the bug is in generated glue code we don't own.
      if (errorMessage === '[object ErrorEvent]') {
        return null;
      }

      // Silence WebAssembly out-of-bounds traps. These come from the SDK's
      // WASM heap being corrupted/exhausted, seen so far only in memory-
      // constrained mobile in-app browsers (e.g. Twitter's iOS WebView) -
      // an environment limitation, not something this app can prevent.
      if (errorMessage.includes('Out of bounds memory access')) {
        return null;
      }

      // Silence 404 errors for recordings that are not shared or not published
      // These are normal cases and not errors we need to track
      const { request } = event;
      const { url } = request || {};
      const is404Error = errorMessage.includes('404');
      if (url && is404Error) {
        // Silence shared_drives endpoint 404s (recording not shared)
        if (url.includes('/shared_drives/')) {
          return null;
        }
        // Silence publication endpoint 404s (recording not published)
        if (url.includes('/recordings/') && url.includes('/publication')) {
          return null;
        }
      }

      // Silence errors explicitly marked as expected/benign at their throw
      // site (e.g. the not-yet-registered-user 404 that AppRoutes/LandingPage
      // use as sign-up control flow) - see markExpectedError above.
      if (isExpectedError(hint.originalException)) {
        return null;
      }

      if (store) {
        try {
          const state = store.getState();
          // SA3-AUTH-17: deep-redact token fields anywhere in the state tree.
          const redactedState = deepRedact(state);
          // Even capped per-node the tree can be large, so check the real
          // serialized size and drop the dump rather than lose the event to a
          // 413. The size itself is reported, since an unusually large state
          // tree is a clue about the recording that triggered the error.
          const serializedChars = JSON.stringify(redactedState)?.length ?? 0;
          event.extra = event.extra || {};
          event.extra = {
            ...event.extra,
            'Redux State':
              serializedChars > MAX_STATE_DUMP_CHARS
                ? `[Omitted: ${serializedChars} chars exceeds the ${MAX_STATE_DUMP_CHARS} char cap]`
                : redactedState,
          };
        } catch {
          console.warn('Failed to serialize redux store.');
        }
      }

      // Batch together OSF request errors
      if (errorMessage) {
        const isPngOsfFailure = errorMessage.includes('png request failed');
        const isJsonOsfFailure = errorMessage.includes('json request failed');
        if (isPngOsfFailure || isJsonOsfFailure) {
          event.fingerprint = ['OSF Frame Request Error'];
        }
      }

      return event;
    },
  });
}

export const sentryBreadcrumbMiddleware =
  () => (next: any) => (action: any) => {
    if (isProd) {
      addBreadcrumb({
        category: 'redux',
        message: action.type,
      });
    }
    return next(action);
  };

export const registerStore = (storeToRegister: Store<State>) => {
  store = storeToRegister;
};
